import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircle } from 'shared/icons';
import InboundInvoicesPanel from '../../components/warehouse/inbound/InboundInvoicesPanel';
import InboundReceiveModal from '../../components/warehouse/inbound/InboundReceiveModal';
import PageHeader from '../../components/layout/PageHeader';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import ErrorCard from '../../components/ui/ErrorCard';
import { DateTimeText, Id, Mono } from '../../components/ui/Mono';
import Select from '../../components/ui/Select';
import { SkeletonTable } from '../../components/ui/Skeleton';
import StandardTable from '../../components/ui/StandardTable';
import { useWarehouseLabels } from '../../components/warehouse/warehouseLabels';
import { useDebounce } from '../../hooks/useDebounce';
import api from '../../services/api';
import { ExtractPaging } from '../../utils/aggregation';
import { FormatInt } from '../../utils/format';

const DEFAULT_PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [15, 30, 50, 100];
const BLANK_CELL = <span className="text-mono-400">—</span>;

// Status da OC (domínio do purchasing) — rótulos locais desta tela.
const PO_STATUS_LABELS = {
    DRAFT: 'Rascunho',
    APPROVAL: 'Em aprovação',
    OPEN: 'Aguardando fornecedor',
    PRODUCTION: 'Em produção',
    INVOICED: 'Faturada',
    SHIPPED: 'Aguardando recebimento',
    RECEIVED: 'Recebida',
    CANCELLED: 'Cancelada',
};

const LINE_STATUS_VARIANTS = {
    PENDING: 'neutral',
    PARTIAL: 'warning',
    OK: 'success',
    OVER: 'danger',
    NOT_ON_ORDER: 'danger',
};

/**
 * /warehouse/inbound — Recebimento contra ORDEM DE COMPRA.
 *
 * Lista de OCs (progresso recebido/pedido) à esquerda; ficha à direita com o
 * CONFRONTO (pedido × confirmado × recebido físico × NF), os recebimentos já
 * registrados, os pallets criados e as NFs do fornecedor. O ato de receber
 * abre o modal (um pallet por confirmação — parcial é o caso normal).
 */
const WarehouseInboundPage = () => {
    const { label } = useWarehouseLabels();
    const [rows, setRows] = useState([]);
    const [paging, setPaging] = useState({ total: 0, limit: DEFAULT_PAGE_SIZE, offset: 0 });
    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
    const [loadError, setLoadError] = useState(null);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [scope, setScope] = useState('receivable');
    const [searchInput, setSearchInput] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [detailError, setDetailError] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [isReceiveOpen, setReceiveOpen] = useState(false);

    const debouncedSearch = useDebounce(searchInput, 300);

    const loadOrders = useCallback(async () => {
        setLoadError(null);
        try {
            const offset = (Math.max(1, page) - 1) * Math.max(1, pageSize);
            const params = { limit: pageSize, offset, scope };
            const search = debouncedSearch.trim();
            if (search) {
                params.search = search;
            }
            const data = await api.getWarehouseInboundPurchaseOrders(params);
            const list = Array.isArray(data?.results) ? data.results : [];
            setRows(list);
            setPaging(ExtractPaging(data, { total: list.length, limit: pageSize, offset }));
        } catch (error) {
            setLoadError(error?.message || 'Erro ao carregar');
            setRows([]);
        } finally {
            setHasLoadedOnce(true);
        }
    }, [page, pageSize, scope, debouncedSearch]);

    useEffect(() => {
        loadOrders();
    }, [loadOrders]);

    const loadDetail = useCallback(async () => {
        if (!selectedId) {
            setDetail(null);
            return;
        }
        setDetailLoading(true);
        setDetailError(null);
        try {
            const data = await api.getWarehouseInboundPurchaseOrder(selectedId);
            setDetail(data || null);
        } catch (error) {
            setDetailError(error?.message || 'Erro ao carregar');
            setDetail(null);
        } finally {
            setDetailLoading(false);
        }
    }, [selectedId]);

    useEffect(() => {
        loadDetail();
    }, [loadDetail]);

    const refreshAll = useCallback(() => {
        loadOrders();
        loadDetail();
    }, [loadOrders, loadDetail]);

    const columns = useMemo(
        () => [
            {
                key: 'code',
                label: label('wms.inbound.col.order'),
                sortable: false,
                getValue: (row) => row.code,
                render: (row) => <Id value={row.code} className="font-medium" />,
            },
            {
                key: 'supplier_name',
                label: label('wms.inbound.col.supplier'),
                sortable: false,
                getValue: (row) => row.supplier_name || '',
                render: (row) => row.supplier_name || BLANK_CELL,
            },
            {
                key: 'progress',
                label: label('wms.inbound.col.progress'),
                sortable: false,
                align: 'right',
                getValue: (row) => `${row.received_qty}/${row.ordered_qty}`,
                render: (row) => {
                    const complete = Number(row.ordered_qty) > 0 && Number(row.received_qty) >= Number(row.ordered_qty);
                    return (
                        <Mono className={complete ? 'text-green-600 dark:text-green-400' : ''}>
                            {FormatInt(row.received_qty)} / {FormatInt(row.ordered_qty)}
                        </Mono>
                    );
                },
            },
            {
                key: 'status',
                label: label('wms.inbound.col.status'),
                sortable: false,
                getValue: (row) => PO_STATUS_LABELS[row.status] || row.status,
                render: (row) => (
                    <Badge variant={row.status === 'RECEIVED' ? 'success' : row.status === 'CANCELLED' ? 'danger' : 'neutral'}>
                        {PO_STATUS_LABELS[row.status] || row.status}
                    </Badge>
                ),
            },
            {
                key: 'created_at',
                label: label('wms.lpn.field.created_at'),
                sortable: false,
                getValue: (row) => row.created_at || '',
                render: (row) => <DateTimeText value={row.created_at} relative />,
            },
        ],
        [label],
    );

    const listContent = !hasLoadedOnce ? (
        <SkeletonTable columns={5} rows={8} />
    ) : loadError ? (
        <ErrorCard
            title={label('wms.inbound.title')}
            message={`${label('wms.common.load_error')}\n${loadError}`}
            action={label('wms.common.retry')}
            actionFn={loadOrders}
        />
    ) : (
        <StandardTable
            rows={rows}
            columns={columns}
            rowKey={(row, index) => row.id || index}
            onRowClick={(row) => setSelectedId(row.id)}
            countLabel={label('wms.inbound.list_title')}
            exportFileName="wms-inbound-ocs"
            emptyMessage={label('wms.inbound.empty')}
            searchPlaceholder={label('wms.inbound.search_placeholder')}
            searchMode="server"
            searchTerm={searchInput}
            onSearchTermChange={setSearchInput}
            sortMode="server"
            paginationMode="server"
            totalItems={paging.total}
            currentPage={page}
            onPageChange={setPage}
            itemsPerPage={pageSize}
            onItemsPerPageChange={setPageSize}
            defaultPageSize={DEFAULT_PAGE_SIZE}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
        />
    );

    const reconciliation = detail?.reconciliation || null;
    const totals = reconciliation?.totals || null;

    const detailPanel = !selectedId ? (
        <div className="rounded-xl border border-dashed border-mono-300 p-8 text-center text-sm text-mono-500 dark:border-mono-700 dark:text-mono-400">
            {label('wms.inbound.select_hint')}
        </div>
    ) : detailLoading && !detail ? (
        <SkeletonTable columns={4} rows={6} />
    ) : detailError ? (
        <ErrorCard title={label('wms.inbound.title')} message={detailError} action={label('wms.common.retry')} actionFn={loadDetail} />
    ) : detail ? (
        <div className="space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-3 rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <div>
                    <div className="flex items-center gap-2">
                        <Id value={detail.order.code} className="text-lg font-semibold" />
                        <Badge variant={detail.order.status === 'RECEIVED' ? 'success' : 'neutral'}>
                            {PO_STATUS_LABELS[detail.order.status] || detail.order.status}
                        </Badge>
                    </div>
                    <p className="mt-1 text-sm text-mono-600 dark:text-mono-400">
                        {detail.order.supplier_name || '—'}
                    </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                    <Button onClick={() => setReceiveOpen(true)} disabled={!detail.order.receivable}>
                        <PlusCircle size={18} className="mr-2" />
                        {label('wms.inbound.receive_action')}
                    </Button>
                    {!detail.order.receivable ? (
                        <p className="max-w-[280px] text-right text-xs text-mono-500 dark:text-mono-400">
                            {label('wms.inbound.not_receivable')}
                        </p>
                    ) : null}
                </div>
            </div>

            {/* Confronto: pedido × confirmado × recebido, linha a linha. */}
            <section className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                    {label('wms.inbound.items_title')}
                </h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="text-left text-xs uppercase tracking-wide text-mono-500 dark:text-mono-400">
                            <tr>
                                <th className="px-2 py-1.5">{label('wms.inbound.field.sku')}</th>
                                <th className="px-2 py-1.5 text-right">{label('wms.inbound.field.ordered')}</th>
                                <th className="px-2 py-1.5 text-right">{label('wms.inbound.field.confirmed')}</th>
                                <th className="px-2 py-1.5 text-right">{label('wms.inbound.field.received')}</th>
                                <th className="px-2 py-1.5 text-right">{label('wms.inbound.field.pending')}</th>
                                <th className="px-2 py-1.5">{label('wms.inbound.col.status')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-mono-100 dark:divide-mono-900">
                            {(reconciliation?.lines || []).map((line) => (
                                <tr key={line.purchase_order_item_id}>
                                    <td className="px-2 py-1.5">
                                        <Mono>{line.sku}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Mono>{FormatInt(Number(line.ordered))}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Mono>{FormatInt(Number(line.confirmed))}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Mono className="font-semibold">{FormatInt(Number(line.received))}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Mono>{FormatInt(Number(line.pending))}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5">
                                        <Badge variant={LINE_STATUS_VARIANTS[line.status] || 'neutral'}>
                                            {label(`wms.inbound.status.${line.status}`)}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                            {(reconciliation?.extra_lines || []).map((line) => (
                                <tr key={`extra-${line.product_id}`} className="bg-red-50/60 dark:bg-red-950/20">
                                    <td className="px-2 py-1.5">
                                        <span className="text-sm">{line.title || line.product_id}</span>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">{BLANK_CELL}</td>
                                    <td className="px-2 py-1.5 text-right">{BLANK_CELL}</td>
                                    <td className="px-2 py-1.5 text-right">
                                        <Mono className="font-semibold">{FormatInt(Number(line.received))}</Mono>
                                    </td>
                                    <td className="px-2 py-1.5 text-right">{BLANK_CELL}</td>
                                    <td className="px-2 py-1.5">
                                        <Badge variant="danger">{label('wms.inbound.status.NOT_ON_ORDER')}</Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {totals ? (
                    <div className="mt-3 grid grid-cols-2 gap-2 border-t border-mono-100 pt-3 text-sm dark:border-mono-900 sm:grid-cols-4">
                        <div>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.inbound.totals.ordered_value')}
                            </p>
                            <Mono>R$ {totals.ordered_value}</Mono>
                        </div>
                        <div>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.inbound.totals.received_value')}
                            </p>
                            <Mono>R$ {totals.received_value}</Mono>
                        </div>
                        <div>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.inbound.totals.invoices_total')}
                            </p>
                            <Mono>{totals.invoices_total === null ? '—' : `R$ ${totals.invoices_total}`}</Mono>
                        </div>
                        <div>
                            <p className="text-xs text-mono-500 dark:text-mono-400">
                                {label('wms.inbound.totals.invoice_delta')}
                            </p>
                            <Mono
                                className={
                                    totals.invoice_delta_value && Number(totals.invoice_delta_value) !== 0
                                        ? 'font-semibold text-amber-600 dark:text-amber-400'
                                        : ''
                                }
                            >
                                {totals.invoice_delta_value === null ? '—' : `R$ ${totals.invoice_delta_value}`}
                            </Mono>
                        </div>
                    </div>
                ) : null}
            </section>

            <InboundInvoicesPanel order={detail.order} invoices={detail.invoices} label={label} onChanged={refreshAll} />

            <section className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                    {label('wms.inbound.receipts_title')}
                </h3>
                {detail.receipts.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.inbound.receipts_empty')}</p>
                ) : (
                    <ul className="space-y-2">
                        {detail.receipts.map((receipt) => (
                            <li
                                key={receipt.id}
                                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-mono-100 px-3 py-2 text-sm dark:border-mono-900"
                            >
                                <div className="flex items-center gap-2">
                                    <Mono>{receipt.product_sku || receipt.product_id}</Mono>
                                    <span className="text-mono-500 dark:text-mono-400">×</span>
                                    <Mono className="font-semibold">{FormatInt(Number(receipt.qty))}</Mono>
                                    {!receipt.purchase_order_item_id ? (
                                        <Badge variant="danger">{label('wms.inbound.status.NOT_ON_ORDER')}</Badge>
                                    ) : null}
                                </div>
                                <div className="flex items-center gap-2 text-xs text-mono-500 dark:text-mono-400">
                                    <Mono>{receipt.lpn_code}</Mono>
                                    {receipt.employee_name ? (
                                        <span>
                                            {receipt.employee_name} (<Mono>{receipt.employee_code}</Mono>)
                                        </span>
                                    ) : null}
                                    <DateTimeText value={receipt.created_at} relative />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </section>

            <section className="rounded-xl border border-mono-200 bg-white p-4 dark:border-mono-800 dark:bg-mono-950">
                <h3 className="mb-3 text-sm font-semibold text-black dark:text-white">
                    {label('wms.inbound.lpns_title')}
                </h3>
                {detail.lpns.length === 0 ? (
                    <p className="text-sm text-mono-500 dark:text-mono-400">{label('wms.lpns.empty')}</p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {detail.lpns.map((lpn) => (
                            <div
                                key={lpn.id}
                                className="flex items-center gap-2 rounded-lg border border-mono-200 px-3 py-1.5 text-sm dark:border-mono-800"
                            >
                                <Mono className="font-medium">{lpn.code}</Mono>
                                {lpn.location_code ? <Badge variant="neutral">{lpn.location_code}</Badge> : null}
                                <Badge variant={lpn.status === 'ACTIVE' ? 'success' : 'neutral'}>{lpn.status}</Badge>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    ) : null;

    return (
        <div className="space-y-4">
            <PageHeader title={label('wms.inbound.title')} subtitle={label('wms.inbound.subtitle')} />

            <div className="flex items-center gap-3">
                <Select value={scope} onChange={(event) => { setScope(event.target.value); setPage(1); }} className="max-w-xs">
                    <option value="receivable">{label('wms.inbound.scope.receivable')}</option>
                    <option value="all">{label('wms.inbound.scope.all')}</option>
                </Select>
            </div>

            <div className="flex flex-col gap-4 2xl:flex-row 2xl:items-start">
                <div className="min-w-0 flex-1">{listContent}</div>
                <aside className="w-full shrink-0 2xl:w-[720px]">{detailPanel}</aside>
            </div>

            {detail ? (
                <InboundReceiveModal
                    isOpen={isReceiveOpen}
                    onClose={() => setReceiveOpen(false)}
                    order={{ ...detail.order, invoices: detail.invoices }}
                    items={detail.items}
                    label={label}
                    onCreated={refreshAll}
                />
            ) : null}
        </div>
    );
};

export default WarehouseInboundPage;
